// Data hooks for the FinOps v2 (/api/finops/v2) endpoints.
//
// Follows the MonitoringContext degradation contract exactly: global axios (JWT
// auto-injected), a per-request timeout, catch → data:null (so a 503 when
// ClickHouse is off simply hides the panel), a mounted ref so a late response
// never setState-after-unmount, and a refetch whenever clusterId / hours / params
// change. The `available` flag distinguishes "ClickHouse disabled (503)" from a
// transient error, so the UI can show the right empty state.

import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

// NOTE: reference API only at CALL TIME (inside a function), never at module
// top-level. App.js imports Monitoring (non-lazy), which pulls in this module, so
// this is a circular import — at module-eval time App's `API` export is not yet
// initialized and reading it would throw, blanking the whole app. Deferring to
// call time (the MonitoringContext pattern) avoids the cycle entirely.
const v2url = (path) => `${API}/finops/v2${path}`;
const TIMEOUT = 20000;

// Core fetcher. `path` is relative to /finops/v2 (e.g. `/clusters/x/nodes`).
export function useFinopsResource(path, { params = {}, enabled = true } = {}) {
  const [state, setState] = useState({ data: null, loading: true, error: null, available: true });
  const mounted = useRef(true);
  // stable key so the effect only refires on real param changes
  const key = JSON.stringify({ path, params, enabled });

  const refetch = useCallback(async () => {
    if (!enabled || !path) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.get(v2url(path), { params, timeout: TIMEOUT });
      if (!mounted.current) return;
      setState({ data: res.data, loading: false, error: null, available: true });
    } catch (err) {
      if (!mounted.current) return;
      const status = err?.response?.status;
      setState({
        data: null, loading: false,
        error: status === 503 ? null : (err?.response?.data?.detail || err.message),
        available: status !== 503,
      });
    }
  }, [key]); // key encodes path+params+enabled; refetch is intentionally keyed on it

  useEffect(() => {
    mounted.current = true;
    refetch();
    return () => { mounted.current = false; };
  }, [refetch]);

  return { ...state, refetch };
}

// Convenience wrappers ------------------------------------------------------

export const useCostTimeseries = (clusterId, hours, groupBy, top = 8) =>
  useFinopsResource(clusterId ? `/clusters/${clusterId}/cost/timeseries` : null,
    { params: { hours, group_by: groupBy, top }, enabled: !!clusterId });

export const useCostBreakdown = (clusterId, hours, groupBy, opts = {}) =>
  useFinopsResource(clusterId ? `/clusters/${clusterId}/cost/breakdown` : null,
    { params: { hours, group_by: groupBy, top: opts.top ?? 100, with_prev: opts.withPrev ?? true },
      enabled: !!clusterId });

export const useNodes = (clusterId, hours) =>
  useFinopsResource(clusterId ? `/clusters/${clusterId}/nodes` : null,
    { params: { hours }, enabled: !!clusterId });

export const useResourceTimeseries = (clusterId, hours) =>
  useFinopsResource(clusterId ? `/clusters/${clusterId}/resource-timeseries` : null,
    { params: { hours }, enabled: !!clusterId });

export const useTopWorkloads = (clusterId, hours, sort = 'waste', limit = 20) =>
  useFinopsResource(clusterId ? `/clusters/${clusterId}/workloads/top` : null,
    { params: { hours, sort, limit }, enabled: !!clusterId });

export const useUnitCosts = (clusterId, hours) =>
  useFinopsResource(clusterId ? `/clusters/${clusterId}/unit-costs` : null,
    { params: { hours }, enabled: !!clusterId });

export const useAnomalies = (clusterId, days = 14) =>
  useFinopsResource(clusterId ? `/clusters/${clusterId}/anomalies` : null,
    { params: { days }, enabled: !!clusterId });

export const useOverview = (hours) =>
  useFinopsResource('/overview', { params: { hours } });
